// Bridge from the business API layer to the shell for the read-restricted
// roles (PRD 89 §4.9): parseEnvelope reports every 403 `role_forbidden` here,
// and LiveBusinessShell subscribes to draw ONE page-level "This isn't part of
// your role here" in place of the page — never a toast per refused request.
//
// The same shape as the read-only, period-lock and firm-required notifiers,
// for the same reason: the API layer knows the answer and cannot draw, and the
// shell can draw and cannot know.
//
// NOT throttled, unlike firm_required: that one navigates, and six refusals
// must not mean six navigations. This one only marks a page, and marking it
// six times is the same as marking it once.

/** One refusal, and which page asked. */
export interface RoleForbiddenNotice {
  /**
   * The path of the page that SENT the refused request (see
   * utils/businessRequestOrigin), or null when that is unknown. A refusal
   * answering a page the person has already left must not blank the one they
   * moved to.
   */
  path: string | null;
  /** The business the request was sent for (its X-Business-Id), or null. */
  businessId: string | null;
  /** The api's own sentence, for the record; the shell has its own copy. */
  message: string;
}

type Listener = (notice: RoleForbiddenNotice) => void;

let listeners = new Set<Listener>();

/** Subscribe to role_forbidden refusals; returns the unsubscribe function. */
export function subscribeRoleForbidden(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Called by the API layer on a 403 role_forbidden response. */
export function notifyRoleForbidden(notice: RoleForbiddenNotice): void {
  listeners.forEach(listener => listener(notice));
}

/** Test hook — clears listeners. */
export function resetRoleForbiddenNotifier(): void {
  listeners = new Set();
}
